import { List, MagnifyingGlass } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { useSidebarStore } from '@/lib/store/sidebarStore';
import { Button } from '@/ui/button';
import { Kbd } from '@/ui/kbd';

import { CommandPalette } from './CommandPalette';
import { EnvSwitcher } from './EnvSwitcher';
import { UserMenu } from './UserMenu';

export function Topbar() {
    const setMobileOpen = useSidebarStore((state) => state.setMobileOpen);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Global ⌘K / Ctrl+K toggles the palette. Skip when the user is typing in
    // an input / textarea / contenteditable so we don't steal native browser
    // shortcuts inside forms.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey)) return;
            const target = e.target as HTMLElement | null;
            const inField =
                target?.tagName === 'INPUT' ||
                target?.tagName === 'TEXTAREA' ||
                target?.isContentEditable === true;
            if (inField) return;
            e.preventDefault();
            setPaletteOpen((v) => !v);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return (
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur sm:gap-4 sm:px-6">
            <Button
                variant="ghost"
                size="icon-sm"
                className="lg:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
            >
                <List weight="regular" className="h-5 w-5" />
            </Button>

            <div className="flex-1">
                <CommandHint onClick={() => setPaletteOpen(true)} />
            </div>

            <EnvSwitcher />
            <UserMenu />

            <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </header>
    );
}

function CommandHint({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="hidden h-9 w-full max-w-xs items-center gap-2 rounded-md border border-border bg-card px-3 text-xs text-muted-foreground transition-colors hover:bg-muted md:inline-flex"
            aria-label="Open command palette"
        >
            <MagnifyingGlass weight="regular" className="h-3.5 w-3.5" />
            <span className="flex-1 text-start">Jump to a page…</span>
            <Kbd>⌘K</Kbd>
        </button>
    );
}
